import {NgStyle} from '@angular/common';
import {Component, inject} from '@angular/core';
import {Subscription} from 'rxjs';
import {QuestionType} from '../../../../enums/consultation/QuestionType';
import {Step} from '../../../../models/consultation/Step';
import {Question} from '../../../../models/Question';
import {ConsultationApiService} from '../../../../services/consultation.api.service';
import {ConsultationService} from '../../../../services/consultation.service';
import {ContactDetailsComponent} from './components/contact-details/contact-details.component';
import {QuestionComponent} from './components/question/question.component';
import {WizardCompletedComponent} from './components/wizard-completed/wizard-completed.component';

@Component({
    selector: 'app-wizard-progress',
    imports: [
        NgStyle,
        QuestionComponent,
        WizardCompletedComponent,
        ContactDetailsComponent
    ],
    templateUrl: './wizard-progress.component.html',
    styleUrl: './wizard-progress.component.scss'
})
export class WizardProgressComponent {

    // region Variables

    public progressSteps: Question[] = [];
    public currentStep: number = 0;
    public activeStep: any | null = null;
    public wizardStarted: boolean = false;
    public surveyStep: Step | undefined;
    private subOnUpdateSurveyStep: Subscription | undefined;
    private subOnUpdateSurveyQuestion: Subscription | undefined;
    private subOnSubmitConsultation: Subscription | undefined;
    private consultationService: ConsultationService = inject(ConsultationService);
    private consultationApi: ConsultationApiService = inject(ConsultationApiService);

    // endregion

    // Constructor
    constructor() {

        this.setProgressSteps();

    }

    // Init
    ngOnInit() {

        this.activeStep = this.progressSteps[0];

        // Update Survey Question Listener
        this.subOnUpdateSurveyQuestion = this.consultationService.onUpdateSurveyQuestion.subscribe((data: any) => {
            this.progressSteps[data.question.index - 1] = {
                ...data.question,
                data: data.question.index === 1
                    ? data.data
                    : {
                        selectedAnswers: data.data.selectedAnswers,
                        answer: data.data.answer,
                        ...(data.data.files?.length > 0 ? {files: data.data.files} : {})
                    }
            };
        });

        // Update Survey Step Listener
        this.subOnUpdateSurveyStep = this.consultationService.onUpdateSurveyStep.subscribe((step: Step) => {
            this.surveyStep = step;
        });

    }

    // Destroy
    ngOnDestroy() {

        if (this.subOnUpdateSurveyQuestion) this.subOnUpdateSurveyQuestion?.unsubscribe();
        if (this.subOnUpdateSurveyStep) this.subOnUpdateSurveyStep?.unsubscribe();
        if (this.subOnSubmitConsultation) this.subOnSubmitConsultation?.unsubscribe();

    }

    // Complete Survey
    completeSurvey() {

        let formData = new FormData();

        if (!this.progressSteps || !Array.isArray(this.progressSteps) || this.progressSteps.length < 1) return;

        const questions: any = [];
        this.progressSteps.forEach((step: Question) => {
            const question = {
                ...(step.type === QuestionType.ContactDetails ? {contactDetails: {...step.data}} : {}),
                type: step.type,
                selectedAnswers: step?.data?.selectedAnswers,
                question: step.text,
                answer: step?.data?.answer
            };
            questions.push(question);
            if (step?.data?.files) {
                for (const file of step?.data?.files) {
                    formData.append('images', file, file.name);
                }
            }
        });
        formData.append('questions', JSON.stringify(questions));

        // Submit Form Data
        this.subOnSubmitConsultation = this.consultationApi.submitConsultation(formData).subscribe({
            next: (response: any) => {
                if (!response) return;
            },
            error: (err: any) => {
            }
        });

    }

    // Exit Consultation
    onExitConsultation() {

        this.setProgressSteps();
        this.wizardStarted = false;
        this.currentStep = 0;

    }

    // Get Navigation Styles
    getNavigationStyles(): object {

        if (this.currentStep > 0) return {
            'grid-template-columns': 'repeat(2, 1fr)'
        };

        return {};

    }

    // Get Progress Amount
    getProgressAmount(): string {

        const totalSteps: number = this.progressSteps.length - 1;
        const currStep: number = this.currentStep + 1;

        if (totalSteps === 0) return '0%';
        if (currStep > totalSteps) return '100%';

        return ((currStep / totalSteps) * 100).toFixed(0) + '%';

    }

    // Next Step
    onNextStep() {

        if ((this.currentStep + 1) <= this.progressSteps.length - 1) {
            this.currentStep += 1;
            this.setActiveStep(this.currentStep);
        }

    }

    // Previous Step
    onPreviousStep() {

        if ((this.currentStep - 1) >= 0) {
            this.currentStep -= 1;
            this.setActiveStep(this.currentStep);
        }

    }

    // Set Active Step
    setActiveStep(step: number) {

        this.consultationService.updateSurveyStep({
            currentStep: this.currentStep,
            maxSteps: this.progressSteps.length - 1,
            data: {}
        });
        if (this.currentStep === this.progressSteps.length - 1) this.completeSurvey();
        this.activeStep = this.progressSteps[step];

    }

    // Set Progress Steps
    setProgressSteps() {

        this.progressSteps = [
            {
                index: 1,
                type: QuestionType.ContactDetails,
                text: 'Customer Details'
            },
            {
                index: 2,
                type: QuestionType.Question,
                text: 'What is your main reason for this video consult? If we’re discussing a particular space, please specify which room in your home and include photos from all 4 corners of that room. I’ll need these at your earliest convenience to get started on the mood board.',
                availableAnswers: [
                    {
                        name: 'Option 1',
                        value: 'option1'
                    },
                    {
                        name: 'Option 2',
                        value: 'option2'
                    },
                    {
                        name: 'Option 3',
                        value: 'option3'
                    }
                ]
            },
            {
                index: 3,
                type: QuestionType.Question,
                text: 'How would you describe your design style/aesthetics?'
            },
            {
                index: 4,
                type: QuestionType.Question,
                text: 'Do you prefer a neutral color palette, or are you open to including pops of color in your home? What colors are you comfortable seeing in your home, outside of neutrals?'
            },
            {
                index: 5,
                type: QuestionType.Question,
                text: 'Are you willing to tackle DIY projects, e.g. , wallpaper installation, painting, and accent walls?'
            },
            {
                index: 6,
                type: QuestionType.Question,
                text: 'Please include any additional information you think will be important to our meeting.'
            },
            {
                index: 7,
                type: QuestionType.Images,
                hasUploader: true,
                text: 'Please Upload Some Images.'
            },
            {
                index: 8,
                type: QuestionType.Completed
            }
        ];

    }

    // Start Consultation
    onStartConsultation() {

        this.wizardStarted = true;

    }

}
